import React from "react";
import { useTranslation } from "react-i18next";
import sendolaPayLogo from "@/assets/Img/logo-sendola-pay.png";
import PersonBlue from "@/assets/Icons/person-blue.svg?react";
import CFELogo from "@/assets/Icons/cfe-logo.svg?react";
import InteracLogo from "@/assets/Icons/Interac.svg?react";
import CheckPayIcon from "@/assets/Icons/check-pay.svg?react";
import ZelleBlackLogo from "@/assets/Icons/zelle-black-logo.svg?react";
import VenmoBlackLogo from "@/assets/Icons/venmo-black-logo.svg?react";
import PayPalBlackLogo from "@/assets/Icons/paypal-black-logo.svg?react";
import classNames from "classnames";

type CountryCode = "USA" | "CAN";

interface SendolaPayCardProps {
  country: CountryCode;
  className?: string;
  onClickLearnMore?: () => void;
}

export const SendolaPayCard: React.FC<SendolaPayCardProps> = ({
  country,
  className,
  onClickLearnMore,
}) => {
  const { t } = useTranslation("cabo");

  const sendAbroadKey =
    country === "CAN"
      ? "solutions.sendola.sendAbroadCAN"
      : "solutions.sendola.sendAbroadUS";
  const payBillsKey =
    country === "CAN"
      ? "solutions.sendola.payBillsCAN"
      : "solutions.sendola.payBillsUS";

  return (
    <div
      className={classNames(
        "bg-white rounded-[2.5rem] shadow-xl shadow-blue-50 w-full mx-auto flex flex-col items-center gap-4 md:gap-6",
        className
      )}
    >
      <img
        src={sendolaPayLogo}
        alt="SendolaPay Logo"
        className="h-16 sm:h-20 mb-2 mt-2"
      />

      <div
        className="w-full rounded-3xl border-[0.5px] border-blue-200 bg-white px-4 py-2 flex flex-col items-center gap-2 scale-[1.02]"
        style={{ boxShadow: "0 0 0 2px #e0e7ff" }}
      >
        <div className="flex items-center gap-3 w-full justify-center">
          <PersonBlue className="w-8 h-8 flex-shrink-0" />
          <div className="flex flex-col text-center">
            <span className="font-semibold sm:text-lg md:text-xl text-black">
              {t("solutions.sendola.forIndividuals")}
            </span>
            <span
              className={classNames(
                "font-bold text-sm sm:text-base md:text-lg text-blue-600 leading-tight",
                { "mb-4": country === "CAN" }
              )}
            >
              {t("solutions.sendola.forDailyTransactions", {
                amount: country === "CAN" ? "999" : "2,999",
              })}
            </span>
            {country === "USA" && (
              <span className="text-xs text-blue-700 mt-1">
                ({t("solutions.sendola.monthlyLimit", { amount: "7,999" })})
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 md:gap-4 w-full mt-2 p-4 md:p-6 sm:p-10">
        <div className="flex items-start gap-2">
          <CheckPayIcon className="h-6 w-6 flex-shrink-0" />
          <span className="text-black text-base sm:text-lg">
            {t(sendAbroadKey)}
          </span>
        </div>
        <div className="flex items-start gap-2">
          <CheckPayIcon className="h-6 w-6 flex-shrink-0" />
          <span className="text-black text-base sm:text-lg flex items-center gap-1">
            {t(payBillsKey)}{" "}
            <CFELogo className="h-9 w-auto inline-block ml-1 flex-shrink-0" />
          </span>
        </div>
        <div className="flex items-start gap-2">
          <CheckPayIcon className="h-6 w-6 flex-shrink-0" />
          <span className="text-black text-base sm:text-lg">
            {t("solutions.sendola.paymentMethods")}
          </span>
        </div>
        <div className="flex flex-row flex-wrap gap-x-4 items-center justify-start ml-8">
          {country === "CAN" ? (
            <>
              <div className="p-2 px-3 bg-[#F0504E] text-white rounded-xl">
                Debit
              </div>
              <div className="p-2 px-3 bg-[#3D3BD5] text-white rounded-xl">
                Credit
              </div>
              <InteracLogo className="h-9 w-auto" />
            </>
          ) : (
            <>
              <ZelleBlackLogo className="h-4 sm:h-6 w-auto" />
              <VenmoBlackLogo className="h-2 sm:h-4 w-auto" />
              <PayPalBlackLogo className="h-4 sm:h-6 w-auto" />
              <span className="text-xl sm:text-2xl font-semibold">ACH*</span>
            </>
          )}
        </div>
        <span className="text-xs text-gray-500 ml-8">
          *
          {country === "CAN"
            ? t("solutions.sendola.payAccountCAN")
            : t("solutions.sendola.payAccount")}
        </span>
      </div>

      <button
        className="mt-auto mb-8 bg-white border border-gray-200 rounded-full px-10 py-2 font-medium text-lg hover:bg-blue-50 transition-all shadow-sm"
        onClick={onClickLearnMore}
      >
        {t("learnMore")}
      </button>
    </div>
  );
};
